import {put, call, all} from 'redux-saga/effects';
import BankAccount from "../../entities/bankAccount";
import * as Actions from "../actions/bankAccountActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type BankAccountAction = {
    bankAccountId: string;
    bankAccount: BankAccount;
    bankAccounts: BankAccount[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneBankAccount(action: BankAccountAction) {
	const session = tabSession.captureGeneration();
	try {
		const bankAccount: BankAccount = yield call(api.findOne, 'bankAccount', action.bankAccountId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.BANKACCOUNT_FETCH_ONE_SUCCESS, bankAccountId: action.bankAccountId, bankAccount, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.BANKACCOUNT_FETCH_ONE_FAILURE, bankAccountId: action.bankAccountId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {BankAccountAction} action
 */
export function* saveBankAccount(action: BankAccountAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ BANKACCOUNT_SAVE saga', action.bankAccount?.id);

		if (action.bankAccount === undefined || action.bankAccount === null) {
			console.error('saveBankAccount received a null');
			throw new Error('saveBankAccount received a null');
		}

		if (!(action.bankAccount instanceof BankAccount)) {
			action.bankAccount = new BankAccount(action.bankAccount);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.bankAccount.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.bankAccount);
			const addResult = yield call(idb.add, 'bankAccount', action.bankAccount);
			if (addResult.isErr()) {
				console.error(`⮂ saveBankAccount: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertBankAccountSave(action.bankAccount, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.BANKACCOUNT_SAVE_FAILURE, bankAccount: action.bankAccount, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.BANKACCOUNT_SAVE, bankAccount: action.bankAccount, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertBankAccountSave(action.bankAccount, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.BANKACCOUNT_SAVE_FAILURE, bankAccount: action.bankAccount, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {BankAccountAction} action
 * @param {BankAccount[]} action.bankAccounts
 */
export function* saveManyBankAccounts(action: BankAccountAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ BANKACCOUNT_SAVE_MANY saga', action.bankAccounts.map(bankAccount => bankAccount.id).join(','));

        if (!Array.isArray(action.bankAccounts)) {
            console.error('saveManyBankAccounts expected an array, received', action.bankAccounts);
            throw new Error('saveManyBankAccounts expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveBankAccount), under these queue keys
		const dispatchedAddedOns: number[] = action.bankAccounts.map(bankAccount => bankAccount?.addedOn);
		const revertAll = () => action.bankAccounts.map((bankAccount, i) =>
			put(Actions.revertBankAccountSave(bankAccount, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: BankAccount[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.bankAccounts.length; i++) {
				const bankAccount = action.bankAccounts[i];
				bankAccount.persisted = false;
				ensureOperationId(bankAccount);
				toSave.push(bankAccount);
			}

			const addResult = yield call(idb.add, 'bankAccount', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyBankAccounts: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.BANKACCOUNT_SAVE_MANY_FAILURE, bankAccounts: action.bankAccounts, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.BANKACCOUNT_SAVE_MANY, bankAccounts: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.BANKACCOUNT_SAVE_MANY_FAILURE, bankAccounts: action.bankAccounts, status: 500, err});

			console.error(`Error saving ${action.bankAccounts.length} bankAccounts to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {BankAccountAction} action
 */
export function* removeBankAccount(action: BankAccountAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.bankAccount === undefined || action.bankAccount === null || !(action.bankAccount instanceof BankAccount)) {
			console.error('removeBankAccount expected type BankAccount, received', action.bankAccount);
			reportQueued(false);
			throw new Error('removeBankAccount expected type BankAccount');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.bankAccount.status = 'D';
		action.bankAccount.addedOn = newHRTimestamp();
		action.bankAccount.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.bankAccount);
		const addResult = yield call(idb.add, 'bankAccount', action.bankAccount);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeBankAccount: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.BANKACCOUNT_REMOVE_FAILURE, bankAccount: action.bankAccount, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.BANKACCOUNT_REMOVE, bankAccount: action.bankAccount});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.BANKACCOUNT_REMOVE_FAILURE, bankAccount: action.bankAccount, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this BankAccount.
	*
	* @param {BankAccountAction} action
	*/
export function* restoreBankAccount(action: BankAccountAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreBankAccount expected getCurrentUserId() to return a value');
        return;
    }

	if (action.bankAccount === undefined || action.bankAccount === null) {
        console.error('restoreBankAccount expected a BankAccount, received null');
        throw new Error('restoreBankAccount expected a BankAccount, received null');
    }
    else if (!(action.bankAccount instanceof BankAccount)) action.bankAccount = new BankAccount(action.bankAccount); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.bankAccount.status = 'U';
		action.bankAccount.addedOn = newHRTimestamp();
		action.bankAccount.addedBy = userId;
		action.bankAccount.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.bankAccount);
		const addResult = yield call(idb.add, 'bankAccount', action.bankAccount);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreBankAccount: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.BANKACCOUNT_RESTORE_FAILURE, bankAccount: action.bankAccount, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.BANKACCOUNT_RESTORE, bankAccount: action.bankAccount});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.BANKACCOUNT_RESTORE_FAILURE, bankAccount: action.bankAccount, status: 500, err});

		console.error('Error restoring from database', err);
	}
}